import { isNil } from '@develop/ia-utils'
import type { RouteLocationNormalized } from '#vue-router'
import type { PharmacyRouteValidationResult } from '~/composables/routing-validator'
import type { GermanOrEnglishGermanRoute } from '~/models/middleware-types'

// Routes to be matched [en, de]
const shortPharmacyPages = {
  'prescription-upload': [
    { name: 'pharmacyId-prescription-upload' },
    { name: 'pharmacyId-rezept-upload' },
  ],
  'products': [
    { name: 'pharmacyId-products' },
    { name: 'pharmacyId-produkte' },
  ],
  // for product page this is already handled in routingValidator automatically (when checking pharmacy slug)
  // as full pharmacy params are passed on further navigation, because we know the exact route to navigate to (product page)
  // but here there could be multiple routes, so we explicitly handle them
} as const satisfies Record<string, GermanOrEnglishGermanRoute>

type ShortPharmacyPageKey = keyof typeof shortPharmacyPages

const matchShortPharmacyPage = (page: ShortPharmacyPageKey, to: RouteLocationNormalized) => {
  return shortPharmacyPages[page].some(route => route.name === to.name)
}

// Handles pharmacy pages that could also have a short route `/[pharmacyId]/[page]`
// and return full pharmacy slug route `/[pharmacyName]-[pharmacyPlz]-[pharmacyId]/[page]`
// which is then navigated in the middleware
export const handleShortPharmacyPages = (pharmacyValidated: PharmacyRouteValidationResult, to: RouteLocationNormalized) => {
  const router = useRouter()

  const isPage = (key: ShortPharmacyPageKey) => matchShortPharmacyPage(key, to)

  // this ensures correct type for correctPharmacyRoute params
  if (!pharmacyValidated.params || Object.values(pharmacyValidated.params).every(isNil)) {
    return undefined
  }
  // Fixes pharmacyName expected type when passing in explicit route name
  const pharmacyValidatedParams = ensurePharmacyParamsType(pharmacyValidated.params!)

  let correctPharmacyRoute: RouteLocationRaw | undefined

  // [pharmacyId]/rezept-upload
  if (isPage('prescription-upload')) {
    correctPharmacyRoute = router.resolve({
      name: resolvePharmacyRouteName('pharmacyName-pharmacyPlz-pharmacyId-rezept-upload', pharmacyValidatedParams),
      params: pharmacyValidatedParams,
      query: to.query,
      hash: to.hash,
    })
  }

  // [pharmacyId]/produkte
  else if (isPage('products')) {
    correctPharmacyRoute = router.resolve({
      name: resolvePharmacyRouteName('pharmacyName-pharmacyPlz-pharmacyId-produkte', pharmacyValidatedParams),
      params: pharmacyValidatedParams,
      query: to.query,
      hash: to.hash,
    })
  }

  return correctPharmacyRoute
}
